import React, {FC, useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {useApiCall, useApiEndpoint} from "../../../../lib/src/api";
import {config} from "../../config";
import moment from "moment";
import {
  EmployeeProgress,
  LearnDashboardStatistic,
  LearnGraphData, TeamProgressGraphStatistics, TeamProgressStatistic
} from "../../../../lib/src/api/repositories/learn-repository";
import {getCategoryChildrenRecursively} from "../../../../lib/src/learn/services/modules";
import {ContentContainer} from "../../../../lib/src/components/content/ContentContainer";
import {Breadcrumbs} from "../../../../lib/src/components/content/Breadcrumbs";
import {Input} from "../../../../lib/src/components/form/Input";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPeopleGroup, faSquareCheck} from "@fortawesome/free-solid-svg-icons";
import {faCheckCircle, faSquare} from "@fortawesome/free-regular-svg-icons";
import {Select} from "../../../../lib/src/components/form/Select";
import {MetricTile} from "../../components/learn/MetricTile";
import {faChartFft, faDoorOpen, faPlay, faUserGraduate} from "@fortawesome/pro-solid-svg-icons";
import {faClipboardCheck} from "@fortawesome/pro-regular-svg-icons";
import {useTeamData} from "../../resources/TeamContext";
import {LineGraph} from "../../components/learn/LineGraph";
import {timeOptions} from "./LearnDashboardsProgramPage";
import {JaneEmployee, useEmployeesData} from "../../resources/EmployeeContext";
import {BarGraph} from "../../components/learn/BarGraph";
import {Autocomplete} from "../../../../lib/src/components/form/auto-complete/Autocomplete";
import { usePersistentState } from "@ventumsoftware/react-toolkit";

export const LearnDashboardTeamPage: FC<{}> = (props) => {
  const {teams} = useTeamData()

  const [searchParams] = useSearchParams();
  const from = searchParams.get('from')
  const to = searchParams.get('to')
  const team = searchParams.get('team')
  const categories = searchParams.get('categories')

  const {learn} = useApiCall(config)
  const { resource: categoryResource, reload: reloadCategories} = useApiEndpoint(() => learn.listCategories())
  const { resource: moduleResource, reload: reloadModules } = useApiEndpoint(() => learn.listModules())

  const [fromDate, setFromDate] = useState<Date>(from ? moment(from, 'DD-MM-yyyy').toDate() : new Date(moment.now()))
  const [toDate, setToDate] = useState<Date>(to ? moment(to, 'DD-MM-yyyy').toDate(): new Date(moment.now()))
  const [selectedCategories, setSelectedCategories] = useState<string[]>(categories ? categories.split(",") : []);
  const [selectedTeam, setSelectedTeam] = useState<string>(team ? team : '')
  const [statistics, setStatistics] = useState<LearnDashboardStatistic|null>(null)


  const teamsMap = useMemo(() => {
    return Object.fromEntries(teams.map((t) => [t.id.toString(), t.name]))
  }, [teams])

  const primaryCategories = useMemo(() => {
    if (categoryResource === undefined) {
      return [];
    }
    return categoryResource.filter((cat) => cat.parent_category_id === null)
  }, [categoryResource])

  const moduleIds = useMemo(() => {
    if (categoryResource === undefined || moduleResource === undefined) {
      return [];
    }

    const selectedCategoryAndChildCategoryIds = selectedCategories.map((sc) => [
      sc,
      ...getCategoryChildrenRecursively(categoryResource, sc).map(c => c.id)
    ]).flat()

    return moduleResource
      .filter(mod => mod.category_id !== null && selectedCategoryAndChildCategoryIds.includes(mod.category_id))
      .map(mod => mod.id);
  }, [selectedCategories, categoryResource, moduleResource])

  const subTeamsMap = useMemo(() => {
    return teams.filter((t) => t.parentObjectId === Number(selectedTeam))
  }, [teams])

  const teamIds = useMemo(() => {
    return [Number(selectedTeam), ...subTeamsMap.map((st) => st.id)]
  }, [team, subTeamsMap])
  const fetchStatistics = async (from: Date, upTo: Date, modules: string[], teams: number[]) => {
    await learn.fetchTeamProgressDashboardStatistics(from, upTo, modules, teams).then((result) => {
      setStatistics(result)
    })
  }

  useEffect(() => {
    if (moduleIds.length > 0) {
      fetchStatistics(fromDate, toDate, moduleIds, teamIds)
    }
  }, [moduleIds, teamIds, fromDate, toDate]);

  return <ContentContainer size={'full'}>
    <div className={'flex items-stretch bg-white border-b border-brand-200 -mx-6 -mt-8 h-20'}>
      <div className={'flex-1 px-8 flex flex-col justify-center'}>
        <div className={'mt-5 -mb-1'}>
          <Breadcrumbs currentPage={'Dashboards'} crumbs={[
            {label: 'Kennisbank', href: '/learn'}
          ]} />
        </div>
        <div className={'py-2 text-2xl font-bold'}>Kennisbank Dashboard</div>
      </div>

      <div className={'flex px-8 py-4 border-l border-brand-200 gap-x-6 items-center'}>
        <div className={'text-brand-900 font-bold mr-2'}>Periode:</div>
        <div className={'text-sm text-brand-primary font-semibold underline hover:cursor-pointer hover:text-red-500'} onClick={() => {
          setFromDate(new Date(moment().subtract(30, 'days').toDate()))
          setToDate(new Date(moment().toDate()))
        }}>30d</div>
        <div className={'text-sm text-brand-primary font-semibold underline hover:cursor-pointer hover:text-red-500'} onClick={() => {
          setFromDate(new Date(moment().subtract(90, 'days').toDate()))
          setToDate(new Date(moment().toDate()))
        }}>90d</div>
        <div className={'w-64'}>
          <Input inline={true} type={'date'} label={'Van:'} value={fromDate} onChange={setFromDate} />
        </div>
        <div className={'w-64'}>
          <Input inline={true} type={'date'} label={'Tot en met:'} value={toDate} onChange={setToDate} />
        </div>
      </div>
    </div>

    {/* Team dropdown */}
    <div className={"flex bg-white border-b border-b-red-200 -mx-6 px-6 py-4 space-x-8 items-center"}>
      <div className={'flex flex-row space-x-3 items-center'}>
        <div>Team:</div>
        <div className={'font-semibold mr-4 text-brand-on-primary-container'}>{teamsMap[selectedTeam]}</div>
      </div>

      {/* Program selector */}
      <div className={'flex flex-row space-x-3 items-center'}>
        <div>Programma(s):</div>
        <div className={'flex flex-row space-x-3'}>
          {primaryCategories.map((category, i) => {
              const isChecked = selectedCategories.includes(category.id)
              return <div key={i} className={'flex flex-row items-center space-x-1 border-2 border-slate-200 rounded-sm py-1.5 px-2 hover:bg-brand-50 hover:cursor-pointer'}
                          onClick={() => {
                            if (isChecked) {
                              setSelectedCategories(prev => prev.filter((cat) => cat != category.id))
                            } else {
                              setSelectedCategories(prev => [...prev, category.id])
                            }
                          }}>
                <FontAwesomeIcon icon={isChecked ? faSquareCheck : faSquare} className={'text-brand-primary'} />
                <div>{category.name}</div>
              </div>
            })}
        </div>
      </div>
    </div>

    <TeamDashboardContent statistics={statistics} from={fromDate} upTo={toDate} moduleIds={moduleIds} teamIds={teamIds} categories={selectedCategories} />
  </ContentContainer>
}

const TeamDashboardContent: FC<{ statistics: LearnDashboardStatistic|null, from: Date, upTo: Date, moduleIds: string[], teamIds: number[], categories: string[] }> = (props) => {
  const {learn} = useApiCall(config)
  const [teamProgress, setTeamProgress] = useState<TeamProgressStatistic|null>(null)

  const fetch = async () => {
    await learn.fetchTeamProgress(
      props.from,
      props.upTo,
      props.moduleIds,
      props.teamIds
    ).then((result) => {
      setTeamProgress(result)
    })
  }

  useEffect(() => {
    if (props.moduleIds.length > 0) {
      fetch()
    }
  }, [props.from, props.upTo, props.moduleIds, props.teamIds])

  return <div className={'mt-10 mx-6'}>
    <div className={'flex flex-col space-y-8'}>
      <div className={'grid grid-cols-5 gap-8'}>
        <MetricTile
          type={'numeric'}
          title={'Medewerkers gestart'}
          subText={'Totaal aantal medewerkers gestart aan het programma.'}
          currentPeriod={props.statistics?.employees_started.this_period ?? 0}
          previousPeriod={props.statistics?.employees_started.previous_period ?? 0}
          icon={faPlay}
        />

        <MetricTile
          type={'numeric'}
          title={'Medewerkers afgerond'}
          subText={"Aantal medewerkers die de programm's hebben afgerond."}
          currentPeriod={props.statistics?.employees_finished.this_period ?? 0}
          previousPeriod={props.statistics?.employees_finished.previous_period ?? 0}
          icon={faClipboardCheck}
        />

        <MetricTile
          type={'numeric'}
          title={'Medewerkers uit dienst'}
          subText={'Aantal medewerkers die binnen deze periode uit dienst zijn getreden.'}
          currentPeriod={props.statistics?.employees_stopped.this_period ?? 0}
          previousPeriod={props.statistics?.employees_stopped.previous_period ?? 0}
          icon={faDoorOpen}
        />

        <MetricTile
          type={'numeric'}
          title={'Certificaten behaald'}
          subText={'Aantal uitgegeven certificaten binnen deze periode.'}
          currentPeriod={props.statistics?.awarded_certificates.this_period ?? 0}
          previousPeriod={props.statistics?.awarded_certificates.previous_period ?? 0}
          icon={faUserGraduate}
        />

        <MetricTile
          type={'percentage'}
          title={'Team voortgang'}
          subText={'Opgestelde voortgang van alle medewerkers in dit team.'}
          currentPeriod={teamProgress?.teamProgress?.currentTeamProgress ?? 0}
          previousPeriod={teamProgress?.teamProgress.previousTeamProgress ?? 0}
          icon={faChartFft}
        />
      </div>

      <div className={'grid grid-cols-3 gap-8'}>
        <TeamProgressGraphTile from={props.from} upTo={props.upTo} moduleIds={props.moduleIds} teamIds={props.teamIds} />
        <ProgramProgressGraphTile from={props.from} upTo={props.upTo} categories={props.categories} teamIds={props.teamIds} />
        <ProgressPerEmployeeTile from={props.from} upTo={props.upTo} moduleIds={props.moduleIds} teamIds={props.teamIds} />
      </div>
    </div>
  </div>
}

const TeamProgressGraphTile: FC<{ from: Date, upTo: Date, moduleIds: string[], teamIds: number[]}> = (props) => {
  const {learn} = useApiCall(config)
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>(Object.keys(timeOptions)[1])
  const [data, setData] = useState<LearnGraphData|null>(null)

  const fetch = async () => {
    await learn.fetchTeamProgressForGraph(
      props.from,
      props.upTo,
      props.moduleIds,
      selectedTimeOption,
      props.teamIds
    ).then((result) => {
      setData(result)
    })
  }

  useEffect(() => {
    if (props.moduleIds.length > 0) {
      fetch()
    }
  }, [props.from, props.upTo, props.moduleIds, selectedTimeOption, props.teamIds])

  const weekStart = moment(props.from).startOf('isoWeek')
  const weekEnd = moment(props.upTo).endOf('isoWeek')

  return <div>
    <div className={'bg-brand-secondary-container rounded-xl px-6 py-5 col-span-1 flex flex-col'}>
      <div className={'flex justify-between mb-2'}>
        <div className={'flex flex-col'}>
          <div className={'text-brand-on-primary-container font-bold'}>Voortgang per tijdvak</div>
          <div className={'flex-1 text-sm text-brand-secondary'}>Team voortgang in {weekStart.format('D MMMM YYYY')} t/m {weekEnd.format('D MMMM YYYY')}</div>
        </div>
        <div>
          <Select label={''} options={timeOptions} value={selectedTimeOption} onChange={setSelectedTimeOption}/>
        </div>
      </div>
      <div className={'flex-1'}></div>
      <div className={'bg-white rounded-sm'}>
        <LineGraph xAxisLabels={data ? Object.keys(data) : ['no data']}
                   yAxisValues={data ?? {}}
                   dataPointLabel={'% afgerond'}
                   yAxisConfiguration={'percentage'}
        />
      </div>
    </div>
  </div>
}

const ProgramProgressGraphTile: FC<{ from: Date, upTo: Date, categories: string[], teamIds: number[]}> = (props) => {
  const {learn} = useApiCall(config)
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>(Object.keys(timeOptions)[1])
  const [data, setData] = useState<TeamProgressGraphStatistics|null>(null)

  const weekStart = moment(props.from).startOf('isoWeek')
  const weekEnd = moment(props.upTo).endOf('isoWeek')

  const fetch = async () => {
    const requests = props.categories.slice(0, 4).map(async (category) => {
      return learn.fetchTeamProgramProgressForGraph(
        props.from,
        props.upTo,
        category,
        selectedTimeOption,
        props.teamIds
      )
    })
    const categoryStatistics = await Promise.all(requests)
    const combinedStatisticsObjectResponses = categoryStatistics.reduce((combined, categoryStatistics) => {
      return {
        ...combined,
        ...categoryStatistics
      }
    }, {})
    setData(combinedStatisticsObjectResponses)
  }

  useEffect(() => {
    if (props.categories.length > 0) {
      fetch()
    }
  }, [props.from, props.upTo, props.categories, selectedTimeOption, props.teamIds])

  return <div>
    <div className={'bg-brand-secondary-container rounded-xl px-6 py-5 col-span-1 flex flex-col'}>
      <div className={'flex justify-between mb-2'}>
        <div className={'flex flex-col'}>
          <div className={'text-brand-on-primary-container font-bold'}>Voortgang per Programma</div>
          <div className={'flex-1 text-sm text-brand-secondary'}>Modules afgerond van {weekStart.format('D MMMM YYYY')} t/m {weekEnd.format('D MMMM YYYY')} (max. 5 categorieën)</div>
        </div>
        <div>
          <Select label={''} options={timeOptions} value={selectedTimeOption} onChange={setSelectedTimeOption}/>
        </div>
      </div>
      <div className={'flex-1'}></div>
      <div className={'bg-white rounded-sm'}>
        <BarGraph dataSets={data ?? {}} />
      </div>
    </div>
  </div>
}


const ProgressPerEmployeeTile: FC<{ from: Date, upTo: Date, moduleIds: string[], teamIds: number[]}> = (props) => {
  const startDateOptions = {
    all: 'Alle medewerkers',
    fromPeriod: 'Gestart sinds begin periode',
  }

  const {learn} = useApiCall(config)
  const [employeesProgress, setEmployeesProgress] = useState<EmployeeProgress[]|null>(null)
  const [selectedStartDate, setSelectedStartDate] = usePersistentState<string>('learn_employee_tile_filter', 'all')

  const fetch = async () => {
    await learn.fetchEmployeesProgress(
      props.from,
      props.upTo,
      props.moduleIds,
      props.teamIds
    ).then((result) => {
      setEmployeesProgress(result)
    })
  }

  useEffect(() => {
    if (props.moduleIds.length > 0) {
      fetch()
    }
  }, [props.from, props.upTo, props.moduleIds, props.teamIds])

  const {employees} = useEmployeesData()

  return <div>
    <div className={'bg-brand-secondary-container rounded-xl px-6 py-5 col-span-1 flex flex-col'}>
      <div className={'flex justify-between mb-2'}>
        <div className={'flex flex-col'}>
          <div className={'text-brand-on-primary-container font-bold'}>Voortgang per Medewerker</div>
        </div>
        <div>
          <Select label={''} options={startDateOptions} value={selectedStartDate} onChange={setSelectedStartDate}/>
        </div>
      </div>
      <div className={'flex-1'}></div>
      <div>
        <table className="w-full">
          <thead>
          <tr>
            <th className="py-3 text-left text-sm font-bold underline text-brand-on-surface-variant">Naam</th>
            <th className="py-3 text-left text-sm font-bold underline text-brand-on-surface-variant">Startdatum</th>
            <th className="py-3 text-left text-sm font-bold underline text-brand-on-surface-variant">Modules</th>
            <th className="py-3 text-left text-sm font-bold underline text-brand-on-surface-variant">Voortgang</th>
          </tr>
          </thead>
          <tbody>
          {employeesProgress
            ?.map((ep) => ({ ep, employee: employees.find((e) => e.id === ep.employeeId) }))
            .filter(({ employee }) => employee?.role === 'Werknemer')
            .filter(({ ep }) =>
              selectedStartDate === 'all' ||
              moment(ep.startDate).isSameOrAfter(moment(props.from).startOf('day'))
            )
            .map(({ ep, employee }) => (
              <EmployeeRow key={ep.employeeId} employeeProgress={ep} employee={employee!} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
}

const EmployeeRow: FC<{ employeeProgress: EmployeeProgress, employee: JaneEmployee }> = (props) => {
  return <tr>
    <td className="py-1 text-sm">{props.employee.name}</td>
    <td className="py-1 text-sm">{moment(props.employeeProgress.startDate).format('D MMM yyyy')}</td>
    <td className="py-1 text-sm">{`${props.employeeProgress.finishedModules} / ${props.employeeProgress.assignedModules}`}</td>
    <td className="py-1 w-36"><ProgressBar progress={props.employeeProgress.finishedModules} total={props.employeeProgress.assignedModules} /></td>
  </tr>
}

const ProgressBar: FC<{ progress: number, total: number }> = ({ progress, total }) => {
  const percentage = total > 0 ? (progress / total) * 100 : 0;
  return <div className={'flex items-center'}>
    <div className="w-28 h-5 p-[2px] border border-brand-primary rounded-full bg-brand-secondary-container">
      <div
        className="h-full bg-brand-primary rounded-full transition-all"
        style={{ width: `${percentage}%` }}
      />
    </div>
    <div className={'ml-1 h-5 w-5 flex items-center justify-center'}>
      {progress === total ? <FontAwesomeIcon icon={faCheckCircle} className={'text-brand-primary h-4'}/> : <></>}
    </div>
  </div>
}
